import { useState } from 'react'
import styled from 'styled-components'

import Spin from 'pared/components/basicUi/spin'
import Table from 'pared/components/basicUi/table'
import COLORS from 'pared/constants/colors'
import { MOBILE_WIDTH } from 'pared/constants/styles'

import EditOnHandUnitTypeModal from './components/EditOnHandUnitTypeModal'
import EditPrepItemModal from './components/EditPrepItemModal'
import EditPrepItemNameModal from './components/EditPrepItemNameModal'
import EditPrepItemOnHandModal from './components/EditPrepItemOnHandModal'
import EditRestDaysModal from './components/EditRestDaysModal'
import MenuItemToPrepItemModal from './components/MenuItemToPrepItemModal'
import usePrepItemBatchStandardsColumns from './hooks/usePrepItemBatchStandardsColumns'
import usePrepItemBatchStandardsDataSource, {
  IPrepItemBatchStandardTableData,
} from './hooks/usePrepItemBatchStandardsDataSource'
import usePrepItemBatchTypes from './hooks/usePrepItemBatchTypes'
import usePrepItemCategoryColumns from './hooks/usePrepItemCategoryColumns'
import usePrepItemCategoryDataSource from './hooks/usePrepItemCategoryDatasource'
import usePrepItemOnHandColumns from './hooks/usePrepItemOnHandColumns'
import usePrepItemOnHandDataSource, {
  IPrepItemOnHandTableData,
} from './hooks/usePrepItemOnHandDataSource'

const Container = styled.div`
  margin-bottom: 40px;
  width: 100%;
  min-width: 800px;
  max-width: 1200px;

  @media ${MOBILE_WIDTH} {
    width: 100%;
    min-width: 400px;
    padding: 0 25px;
  }
`

const Title = styled.h2`
  margin: 0 0 25px 0;
  font-family: Lexend-SemiBold;
  font-size: 20px;
  font-weight: 600;
  color: ${COLORS.Chalkboard};

  @media ${MOBILE_WIDTH} {
    font-size: 20px;
    margin-bottom: 20px;
  }
`

const Description = styled.p`
  margin: 8px 0 16px 0;
  font-family: Lexend-Regular;
  font-size: 14px;
  color: ${COLORS.Chalkboard};
  font-style: italic;
`

const rowStyle = `
  &:nth-child(even) {
    background-color: ${COLORS.Porcelain};
  }
`

const PrepItemConfiguration = () => {
  const [modalVisible, setModalVisible] = useState(false)
  const [selectedRecord, setSelectedRecord] =
    useState<IPrepItemBatchStandardTableData | null>(null)
  const [onHandModalVisible, setOnHandModalVisible] = useState(false)
  const [selectedOnHandRecord, setSelectedOnHandRecord] =
    useState<IPrepItemOnHandTableData | null>(null)
  const [menuItemModalVisible, setMenuItemModalVisible] = useState(false)
  const [selectedPrepItemId, setSelectedPrepItemId] = useState<string>('')
  const [selectedPrepItemName, setSelectedPrepItemName] = useState<string>('')
  const [nameModalVisible, setNameModalVisible] = useState(false)
  const [selectedNamePrepItemId, setSelectedNamePrepItemId] =
    useState<string>('')
  const [selectedNamePrepItemName, setSelectedNamePrepItemName] =
    useState<string>('')
  const [restDaysModalVisible, setRestDaysModalVisible] = useState(false)
  const [selectedRestDaysPrepItemId, setSelectedRestDaysPrepItemId] =
    useState<string>('')
  const [selectedRestDaysPrepItemName, setSelectedRestDaysPrepItemName] =
    useState<string>('')
  const [selectedRestDaysValue, setSelectedRestDaysValue] = useState<number>(0)
  const [onHandUnitModalVisible, setOnHandUnitModalVisible] = useState(false)
  const [selectedOnHandUnitPrepItemId, setSelectedOnHandUnitPrepItemId] =
    useState<string>('')
  const [selectedOnHandUnitPrepItemName, setSelectedOnHandUnitPrepItemName] =
    useState<string>('')

  const {
    dataSource: prepItemCategoryDataSource,
    loading: prepItemCategoryLoading,
    refetch: refetchPrepItemCategory,
  } = usePrepItemCategoryDataSource()

  const {
    dataSource: prepItemBatchStandardsDataSource,
    loading: prepItemBatchStandardsLoading,
    refetch: refetchPrepItemBatchStandards,
  } = usePrepItemBatchStandardsDataSource()

  const { batchTypes } = usePrepItemBatchTypes()

  const {
    dataSource: prepItemOnHandDataSource,
    loading: prepItemOnHandLoading,
    refetch: refetchPrepItemOnHand,
  } = usePrepItemOnHandDataSource()

  const handleOnHandEditClick = (record: IPrepItemOnHandTableData) => {
    setSelectedOnHandRecord(record)
    setOnHandModalVisible(true)
  }

  const handleOnHandModalClose = () => {
    setOnHandModalVisible(false)
    setSelectedOnHandRecord(null)
  }

  const handleMenuItemDetailsClick = (
    prepItemId: string,
    prepItemName: string,
  ) => {
    setSelectedPrepItemId(prepItemId)
    setSelectedPrepItemName(prepItemName)
    setMenuItemModalVisible(true)
  }

  const handleMenuItemModalClose = () => {
    setMenuItemModalVisible(false)
    setSelectedPrepItemId('')
    setSelectedPrepItemName('')
  }

  const handleNameEditClick = (prepItemId: string, prepItemName: string) => {
    setSelectedNamePrepItemId(prepItemId)
    setSelectedNamePrepItemName(prepItemName)
    setNameModalVisible(true)
  }

  const handleNameModalClose = () => {
    setNameModalVisible(false)
    setSelectedNamePrepItemId('')
    setSelectedNamePrepItemName('')
  }

  const handleRestDaysEditClick = (
    prepItemId: string,
    prepItemName: string,
    restDays: number,
  ) => {
    setSelectedRestDaysPrepItemId(prepItemId)
    setSelectedRestDaysPrepItemName(prepItemName)
    setSelectedRestDaysValue(restDays)
    setRestDaysModalVisible(true)
  }

  const handleRestDaysModalClose = () => {
    setRestDaysModalVisible(false)
    setSelectedRestDaysPrepItemId('')
    setSelectedRestDaysPrepItemName('')
    setSelectedRestDaysValue(0)
  }

  const handleOnHandUnitEditClick = (
    prepItemId: string,
    prepItemName: string,
  ) => {
    setSelectedOnHandUnitPrepItemId(prepItemId)
    setSelectedOnHandUnitPrepItemName(prepItemName)
    setOnHandUnitModalVisible(true)
  }

  const handleOnHandUnitModalClose = () => {
    setOnHandUnitModalVisible(false)
    setSelectedOnHandUnitPrepItemId('')
    setSelectedOnHandUnitPrepItemName('')
  }

  const prepItemCategoryColumns = usePrepItemCategoryColumns({
    onMenuItemDetailsClick: handleMenuItemDetailsClick,
    onEditClick: handleNameEditClick,
    onRestDaysEditClick: handleRestDaysEditClick,
    onOnHandUnitEditClick: handleOnHandUnitEditClick,
  })

  const prepItemOnHandColumns = usePrepItemOnHandColumns({
    onEditClick: handleOnHandEditClick,
    dataSource: prepItemOnHandDataSource,
  })

  const handleEditClick = (record: IPrepItemBatchStandardTableData) => {
    setSelectedRecord(record)
    setModalVisible(true)
  }

  const handleModalClose = () => {
    setModalVisible(false)
    setSelectedRecord(null)
  }

  const prepItemBatchStandardsColumns = usePrepItemBatchStandardsColumns({
    onEditClick: handleEditClick,
  })

  return (
    <>
      <Container>
        <Title>Prep Items</Title>
        <Description>
          See how each Menu Item is correlated to a specific Prep Item
        </Description>
        <Spin spinning={prepItemCategoryLoading}>
          <Table
            columns={prepItemCategoryColumns}
            dataSource={prepItemCategoryDataSource}
            pagination={{ pageSize: 15 }}
            rowStyle={rowStyle}
            rowKey="prepItem"
          />
        </Spin>
      </Container>

      <Container>
        <Title>Prep Item Standard Unit</Title>
        <Description>
          Tells us how many units (ounces/each/...etc) per Batch
        </Description>
        <Spin spinning={prepItemBatchStandardsLoading}>
          <Table
            columns={prepItemBatchStandardsColumns}
            dataSource={prepItemBatchStandardsDataSource}
            pagination={{ pageSize: 15 }}
            rowStyle={rowStyle}
            rowKey="prepItem"
          />
        </Spin>
      </Container>

      <Container>
        <Title>Prep Item On Hand</Title>
        <Description>
          When user Counts what is On Hand, we need to sum these counts and
          convert them to Batch
        </Description>
        <Spin spinning={prepItemOnHandLoading}>
          <Table
            columns={prepItemOnHandColumns}
            dataSource={prepItemOnHandDataSource}
            pagination={{ pageSize: 15 }}
            rowStyle={rowStyle}
            rowKey="prepItem"
          />
        </Spin>
      </Container>

      <EditPrepItemModal
        visible={modalVisible}
        onClose={handleModalClose}
        record={selectedRecord}
        batchTypes={batchTypes}
        onSuccess={() => {
          refetchPrepItemBatchStandards()
        }}
      />

      <EditPrepItemOnHandModal
        visible={onHandModalVisible}
        onClose={handleOnHandModalClose}
        record={selectedOnHandRecord}
        batchTypes={batchTypes}
        onSuccess={() => {
          refetchPrepItemOnHand()
        }}
      />

      <MenuItemToPrepItemModal
        visible={menuItemModalVisible}
        onClose={handleMenuItemModalClose}
        prepItemId={selectedPrepItemId}
        prepItemName={selectedPrepItemName}
      />

      <EditPrepItemNameModal
        visible={nameModalVisible}
        onClose={handleNameModalClose}
        prepItemId={selectedNamePrepItemId}
        prepItemName={selectedNamePrepItemName}
        onSuccess={() => {
          refetchPrepItemCategory()
          refetchPrepItemBatchStandards()
          refetchPrepItemOnHand()
        }}
      />

      <EditRestDaysModal
        visible={restDaysModalVisible}
        onClose={handleRestDaysModalClose}
        prepItemId={selectedRestDaysPrepItemId}
        prepItemName={selectedRestDaysPrepItemName}
        currentRestDays={selectedRestDaysValue}
        onSuccess={() => {
          refetchPrepItemCategory()
        }}
      />

      <EditOnHandUnitTypeModal
        visible={onHandUnitModalVisible}
        onClose={handleOnHandUnitModalClose}
        prepItemId={selectedOnHandUnitPrepItemId}
        prepItemName={selectedOnHandUnitPrepItemName}
        onSuccess={() => {
          refetchPrepItemCategory()
        }}
      />
    </>
  )
}

export default PrepItemConfiguration
